import { useEffect, useState } from "react";
import { VariantImages } from "@/components/catalog/VariantImages";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Pencil } from "lucide-react";
import { LifecycleActions } from "@/components/catalog/LifecycleActions";
import { ProductEdit } from "@/components/catalog/ProductEdit";
import { MosaicActivationPanel } from "@/components/catalog/MosaicActivationPanel";
import { tileSize, meshSize } from "@/lib/dims";
import { useFormats } from "@/lib/useFormats";

const EMPTY_VARIANT = {
  sku: "",
  color_name: "",
  color_code: "",
  format_in: "",
  format_mm: "",
  finish: "",
  presentation: "Loose",
  mesh_sheet_dims: "",
  box_coverage_sqft: "",
  pieces_per_box: "",
  pricing_eligible: false,
  image_url: "",
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [variantForm, setVariantForm] = useState(EMPTY_VARIANT);
  const [saving, setSaving] = useState(false);
  const [specEdit, setSpecEdit] = useState(null); // {variant, form}
  const [history, setHistory] = useState([]);
  const formats = useFormats();
  const fmtOf = (v) => formats.find((f) => f.code === String(v?.format_in || "").toLowerCase());

  const openSpecEdit = async (v) => {
    setSpecEdit({
      variant: v,
      form: {
        format_mm: v.format_mm || "",
        presentation: v.presentation || "",
        mesh_sheet_dims: v.mesh_sheet_dims || "",
        pieces_per_box: v.pieces_per_box ?? "",
        box_coverage_sqft: v.box_coverage_sqft ?? "",
      },
    });
    try {
      const { data } = await api.get(`/admin/catalog/variants/${v.id}/history`);
      setHistory(data);
    } catch {
      setHistory([]);
    }
  };

  const handleSaveSpecs = async (e) => {
    e.preventDefault();
    setSaving(true);
    const f = specEdit.form;
    try {
      await api.patch(`/variants/${specEdit.variant.id}`, {
        presentation: f.presentation,
        ...(f.pieces_per_box !== "" && f.presentation !== "Mesh-mounted" ? { pieces_per_box: parseInt(f.pieces_per_box, 10) } : {}),
        ...(f.box_coverage_sqft !== "" ? { box_coverage_sqft: parseFloat(f.box_coverage_sqft) } : {}),
      });
      toast.success("Product specifications saved");
      setSpecEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/products/${id}`);
      if (data.design?.design_id) { navigate(`/admin/collections/${data.design.design_id}`, { replace: true }); return; }
      setProduct(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/admin/products");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddVariant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/products/${id}/variants`, {
        sku: variantForm.sku,
        color_name: variantForm.color_name,
        color_code: variantForm.color_code,
        format_in: variantForm.format_in,
        format_mm: variantForm.format_mm,
        finish: variantForm.finish,
        presentation: variantForm.presentation,
        mesh_sheet_dims: variantForm.mesh_sheet_dims,
        box_coverage_sqft: parseFloat(variantForm.box_coverage_sqft),
        ...(variantForm.pieces_per_box ? { pieces_per_box: parseInt(variantForm.pieces_per_box, 10) } : {}),
        pricing_eligible: variantForm.pricing_eligible,
        image_urls: variantForm.image_url ? [variantForm.image_url] : [],
      });
      toast.success("Variant created");
      setAddOpen(false);
      setVariantForm(EMPTY_VARIANT);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreReservation = async (variantId, value) => {
    try {
      await api.patch(`/variants/${variantId}`, { allow_pre_reservation: value });
      toast.success(value ? "Pre-reservation enabled" : "Pre-reservation disabled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!product) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="product-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const set = (key) => (e) => setVariantForm((f) => ({ ...f, [key]: e.target.value }));
  const talia = (product.variants || []).filter((v) => /^TAL/.test(v.sku));
  const rows = (product.variants || []).filter((v) => !/^TAL/.test(v.sku));

  return (
    <div data-testid="product-detail-page">
      <Button variant="ghost" onClick={() => navigate("/admin/products")} data-testid="back-to-products" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> All products
      </Button>

      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta" data-testid="product-family-label">{product.design ? "Mosaic Collection" : product.product_type}</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl" data-testid="product-title">{product.display_name || product.name}</h1>
      {product.description && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">{product.description}</p>}
      <p className="mt-2 text-xs text-warmgrey" data-testid="product-record-name">Product name: <span className="text-charcoal">{product.name}</span></p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {product.archived && <span className="badge-status badge-reserved" data-testid="product-archived-badge">archived — hidden from ordering</span>}
        <ProductEdit product={product} onSaved={load} />
        <LifecycleActions kind="product" id={product.id} archived={product.archived} label={product.name} testid="product-lifecycle"
          onDone={(m) => (m === "delete" ? navigate("/admin/products") : load())} />
      </div>

      <div className="mt-10 flex items-center justify-between">
        <p className="text-sm text-warmgrey">
          Variants are the sellable unit — unique by SKU + format + presentation.
        </p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-variant-button" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">
              <Plus className="mr-2 h-4 w-4" /> Add Variant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium text-charcoal">Add variant</DialogTitle>
              <DialogDescription className="text-sm text-warmgrey">
                Box coverage (sqft) is required — all box math uses it.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddVariant} className="mt-2 space-y-5" data-testid="add-variant-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">SKU *</Label>
                  <Input required data-testid="variant-sku-input" value={variantForm.sku} onChange={set("sku")} placeholder="ZW-44-GL-L" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Color *</Label>
                  <Input required data-testid="variant-color-input" value={variantForm.color_name} onChange={set("color_name")} placeholder="Fez White" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Color code</Label>
                  <Input data-testid="variant-color-code-input" value={variantForm.color_code} onChange={set("color_code")} placeholder="MZ004" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Tile size (inches, e.g. 4x4) *</Label>
                  <Input required data-testid="variant-format-input" value={variantForm.format_in} onChange={set("format_in")} placeholder="4x4" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Metric reference (internal, optional)</Label>
                  <Input data-testid="variant-format-mm-input" value={variantForm.format_mm} onChange={set("format_mm")} placeholder="100x100" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Finish</Label>
                  <Input data-testid="variant-finish-input" value={variantForm.finish} onChange={set("finish")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Presentation</Label>
                  <Input data-testid="variant-presentation-input" value={variantForm.presentation} onChange={set("presentation")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Mesh sheet size (inches, e.g. 12" x 12")</Label>
                  <Input data-testid="variant-mesh-input" value={variantForm.mesh_sheet_dims} onChange={set("mesh_sheet_dims")} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Box coverage (sqft) *</Label>
                  <Input required type="number" step="0.01" min="0.01" data-testid="variant-coverage-input" value={variantForm.box_coverage_sqft} onChange={set("box_coverage_sqft")} placeholder="10.8" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Pieces / box</Label>
                  <Input type="number" min="1" data-testid="variant-pieces-input" value={variantForm.pieces_per_box} onChange={set("pieces_per_box")} placeholder="100" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm text-charcoal" data-testid="variant-pricing-eligible-row">
                <Switch checked={variantForm.pricing_eligible} onCheckedChange={(v) => setVariantForm((f) => ({ ...f, pricing_eligible: v }))} data-testid="variant-pricing-eligible" />
                Standard pricing engine (4×4 / 2×6 / 2×2 matrix + premiums)
              </label>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Image URL</Label>
                <Input data-testid="variant-image-input" value={variantForm.image_url} onChange={set("image_url")} placeholder="https://…" className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
              <Button type="submit" disabled={saving} data-testid="add-variant-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create variant"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
        <Table data-testid="variants-table" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">SKU</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Color</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Format</TableHead>
              {!product.design && <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Finish</TableHead>}
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Presentation</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Sqft/box</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Photos</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Pre-reserve</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Specs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground" data-testid="variants-empty">No variants yet.</TableCell></TableRow>
            ) : (
              rows.map((v) => (
                <TableRow key={v.id} className="border-border" data-testid={`variant-row-${v.sku}`}>
                  <TableCell className="py-4 font-mono text-xs text-charcoal">{v.sku}</TableCell>
                  <TableCell className="py-4 text-sm text-charcoal" data-testid={`variant-color-${v.sku}`}>
                    <span>{v.color_name}</span>{v.color_code ? <span className="ml-1 font-mono text-xs text-muted-foreground">({v.color_code})</span> : null}
                    {v.composition
                      ? <span className="ml-2 text-xs text-charcoal" data-testid={`variant-composition-${v.sku}`}>· {v.composition.map((c) => (v.design_id ? c.moroccan_name : c.us_name || `${c.moroccan_name} (U.S. name pending)`)).join(" / ")}</span>
                      : v.design_id ? null
                      : v.us_color_name
                      ? <span className="ml-2 font-semibold text-terracotta" data-testid={`variant-us-name-${v.sku}`}>· {v.us_color_name}</span>
                      : <span className="ml-2 badge-status badge-reserved" data-testid={`variant-us-pending-${v.sku}`}>U.S. name pending</span>}
                  </TableCell>
                  <TableCell className="py-4 text-sm text-warmgrey" data-testid={`variant-dims-${v.sku}`}>{tileSize(v) || "Not set"}{v.presentation === "Mesh-mounted" && meshSize(v) ? <span className="block text-[11px]">Mesh sheet {meshSize(v)}</span> : null}</TableCell>
                  {!product.design && <TableCell className="py-4 text-sm text-warmgrey">{v.finish}</TableCell>}
                  <TableCell className="py-4 text-sm text-warmgrey">{v.presentation}</TableCell>
                  <TableCell className="py-4 text-sm text-warmgrey">{v.box_coverage_sqft}</TableCell>
                  <TableCell className="py-4"><VariantImages variant={v} onChange={load} /></TableCell>
                  <TableCell className="py-4">
                    <Switch
                      checked={v.allow_pre_reservation}
                      onCheckedChange={(val) => handleTogglePreReservation(v.id, val)}
                      data-testid={`pre-reserve-toggle-${v.sku}`}
                    />
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex flex-wrap items-center gap-2">
                    {v.archived && <span className="badge-status badge-reserved" data-testid={`variant-archived-${v.sku}`}>archived</span>}
                    {!v.archived && v.status === "INACTIVE" && <span className="badge-status badge-neutral" data-testid={`variant-hidden-${v.sku}`}>{v.requires_pricing && !v.design_id ? "inactive · hidden · needs price" : "inactive · hidden"}</span>}
                    <Button size="sm" variant="outline" onClick={() => openSpecEdit(v)} data-testid={`edit-specs-${v.sku}`} className="h-8 gap-1 border-border text-charcoal hover:text-terracotta">
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <LifecycleActions kind="variant" id={v.id} archived={v.archived} label={v.sku} testid={`variant-lifecycle-${v.sku}`} onDone={(m, k) => (m === "delete" && k === "product" ? navigate("/admin/products") : load())} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {talia.length > 0 && <p className="border-t border-border px-4 py-3 text-xs text-warmgrey" data-testid="talia-moved-note">TALIA variation {talia.map((v) => v.sku).join(", ")} is managed under <button onClick={() => navigate("/admin/collections/1x4")} className="text-terracotta underline" data-testid="talia-collection-link">Mosaic Collections → TALIA</button>.</p>}
        {product.design && <MosaicActivationPanel productId={product.id} onChanged={load} />}
      </div>

      <Dialog open={!!specEdit} onOpenChange={(o) => !o && setSpecEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-lg" data-testid="edit-specs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Product specifications · {specEdit?.variant.sku}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Governed product master attributes. Changes are audited and propagate to Inventory, Containers, Pricing display and ZELI; historical transaction snapshots are never rewritten.
            </DialogDescription>
          </DialogHeader>
          {specEdit && (
            <form onSubmit={handleSaveSpecs} className="mt-2 space-y-5" data-testid="edit-specs-form">
              {specEdit.variant.presentation === "Mesh-mounted" && (
                <div className="rounded-md border border-border bg-sand/30 p-3 text-xs text-charcoal" data-testid="spec-mesh-packaging">
                  Mesh-mounted packaging (set on the {fmtOf(specEdit.variant)?.label || "format"}, Pricing → Product formats): pieces per sheet {fmtOf(specEdit.variant)?.pieces_per_sheet ?? "not set"} · sheets per box {fmtOf(specEdit.variant)?.sheets_per_box ?? "not set"} · total pieces per box {specEdit.variant.pieces_per_box ?? "—"}
                </div>
              )}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {[
                  ["presentation", "Presentation", "text", "Loose / Mesh-mounted"],
                  ...(specEdit.variant.presentation === "Mesh-mounted" ? [] : [["pieces_per_box", "Pieces / box", "number", "100"]]),
                  ["box_coverage_sqft", "Sq ft / box", "number", "10.76"],
                ].map(([key, label, type, ph]) => (
                  <div className="space-y-2" key={key}>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{label}</Label>
                    <Input
                      type={type}
                      step={type === "number" ? "0.01" : undefined}
                      min={type === "number" ? "0.01" : undefined}
                      data-testid={`spec-${key}-input`}
                      value={specEdit.form[key]}
                      onChange={(e) => setSpecEdit((s) => ({ ...s, form: { ...s.form, [key]: e.target.value } }))}
                      placeholder={ph}
                      className="h-11 bg-white focus-visible:ring-terracotta"
                    />
                  </div>
                ))}
              </div>
              <Button type="submit" disabled={saving} data-testid="edit-specs-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save specifications"}
              </Button>
              <div data-testid="spec-history">
                <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Audit history</p>
                {history.length === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="spec-history-empty">No recorded changes.</p>
                ) : (
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-charcoal">
                    {history.map((h) => (
                      <li key={h.id} data-testid="spec-history-row" className="rounded border border-border px-2 py-1">
                        <span className="font-mono text-muted-foreground">{(h.at || "").slice(0, 19).replace("T", " ")}</span> · {h.type} · {JSON.stringify(h.new_value)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
